import { ApolloError } from '@apollo/client'
import type {
  MinervaAskInput,
  MinervaMetricInput,
  MinervaViewContextInput,
} from '@backoffice-frontend/graphql'
import type { MinervaActivityQuery } from '../utils/minervaDataRequest'
import type { GlobalCubeFilters } from './CubeContext'
import type { MinervaNavigation } from './minervaNavigation'

/** The backend's `MAX_TURN_BYTES`, counted in UTF-8 bytes, not characters. */
export const MINERVA_QUESTION_MAX_BYTES = 8 * 1024

/** The counter shows once a question passes this share of the limit. */
const MINERVA_QUESTION_COUNTER_THRESHOLD = 0.9

export const getMinervaQuestionBytes = (question: string) =>
  new TextEncoder().encode(question).length

export const isMinervaQuestionTooLong = (question: string) =>
  getMinervaQuestionBytes(question) > MINERVA_QUESTION_MAX_BYTES

export const shouldShowMinervaQuestionCounter = (question: string) =>
  getMinervaQuestionBytes(question) >
  MINERVA_QUESTION_MAX_BYTES * MINERVA_QUESTION_COUNTER_THRESHOLD

export type MinervaChatContent = {
  title: string | null
  summary: string
  /** Fixed when the chat opens. A later timeframe change is not sent. */
  viewContext: MinervaViewContextInput | undefined
  metrics: MinervaMetricInput[] | undefined
  /**
   * The dashboard's filters (fleet, region, line …), fixed when the chat
   * opens like `viewContext`. Data requests run under them, as the tiles do.
   */
  globalFilters: GlobalCubeFilters | undefined
}

/** One data request minerva made on the way to an answer. */
export type MinervaChatActivity = {
  /** The model's one-line why. Null when it gave none. */
  reason: string | null
  queries: MinervaActivityQuery[]
}

export type MinervaChatMessage = {
  /** The turn's `messageId`, shared by a question and its answer. */
  messageId: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  status?: 'stopped' | 'failed' | 'fetching-data'
  /** An answer's data requests, in order, shown above it. */
  activities?: MinervaChatActivity[]
  /** The area an answer offers to open, shown below it. */
  navigation?: MinervaNavigation
  /**
   * Set on the pair of messages a timeframe switch prompt leaves behind: the
   * user's pick and the chat's reply. They are made here, not by the backend,
   * and render from this instead of `content`.
   */
  timeframeChoice?: MinervaTimeframeChoice
}

export type MinervaTimeframeChoice = {
  choice: 'switch' | 'keep'
  /** The timeframe the chat answered on before the choice. */
  from: string
  /** The timeframe the dashboard shows. */
  to: string
}

export type MinervaChatEntry = MinervaChatContent & {
  panel: 'open' | 'closed'
  /**
   * False after a switch to another timeframe: the summary describes the
   * timeframe the chat opened with, so the new chat starts without it.
   */
  sendsCardSummary: boolean
  /**
   * The dashboard timeframe the user chose to keep chatting past. Hides the
   * switch prompt until the timeframe changes again.
   */
  keptTimeframe: string | null
  /** Null until the first answer arrives. */
  chatId: string | null
  messages: MinervaChatMessage[]
  /**
   * Set while an answer loads, new for every attempt. The backend can't cancel
   * a running request, so an answer whose attempt is no longer pending is
   * dropped when it arrives.
   */
  pendingRequest: string | null
  /** The backend answered `CHAT_TURN_LIMIT`, no more questions fit. */
  turnLimitReached: boolean
  /** The data requests of the pending question, moved onto its answer. */
  pendingActivities: MinervaChatActivity[]
}

/** One chat per view, keyed by `getMinervaChatViewKey`. */
export type MinervaChats = Record<string, MinervaChatEntry>

export const getMinervaChatViewKey = ({
  viewId,
  panelId,
  serviceAreaUuid,
  fleetId,
}: {
  viewId: string
  panelId: string
  serviceAreaUuid?: string
  fleetId?: string
}) => [viewId, panelId, serviceAreaUuid ?? '', fleetId ?? ''].join(':')

/** A question the user typed, not their pick in a timeframe switch prompt. */
const isMinervaQuestion = (message: MinervaChatMessage) =>
  message.role === 'user' && !message.timeframeChoice

const updateChat = (
  chats: MinervaChats,
  key: string,
  update: (chat: MinervaChatEntry) => MinervaChatEntry,
): MinervaChats => {
  const chat = chats[key]
  return chat ? { ...chats, [key]: update(chat) } : chats
}

/**
 * Opens the chat for `key`, or starts it with `content` the first time. Only
 * one panel is open at a time, so any other open chat is closed.
 */
export const openMinervaChat = (
  chats: MinervaChats,
  key: string,
  content: MinervaChatContent,
): MinervaChats => {
  const closedChats = Object.fromEntries(
    Object.entries(chats).map(([chatKey, chat]) => [
      chatKey,
      { ...chat, panel: 'closed' as const },
    ]),
  )
  const chat: MinervaChatEntry = chats[key] ?? {
    ...content,
    sendsCardSummary: true,
    keptTimeframe: null,
    chatId: null,
    messages: [],
    pendingRequest: null,
    turnLimitReached: false,
    pendingActivities: [],
    panel: 'open',
  }
  return { ...closedChats, [key]: { ...chat, panel: 'open' } }
}

export const closeMinervaChat = (
  chats: MinervaChats,
  key: string,
): MinervaChats => {
  if (chats[key]?.panel !== 'open') return chats
  return updateChat(chats, key, chat => ({ ...chat, panel: 'closed' }))
}

export const addMinervaQuestion = (
  chats: MinervaChats,
  key: string,
  {
    messageId,
    question,
    timestamp,
    pendingRequest,
  }: {
    messageId: string
    question: string
    timestamp: Date
    pendingRequest: string
  },
): MinervaChats =>
  updateChat(chats, key, chat => ({
    ...chat,
    pendingRequest,
    pendingActivities: [],
    messages: [
      ...chat.messages,
      { messageId, role: 'user', content: question, timestamp },
    ],
  }))

export const retryMinervaQuestion = (
  chats: MinervaChats,
  key: string,
  { messageId, pendingRequest }: { messageId: string; pendingRequest: string },
): MinervaChats =>
  updateChat(chats, key, chat => ({
    ...chat,
    pendingRequest,
    pendingActivities: [],
    messages: chat.messages.map(message =>
      message.role === 'user' && message.messageId === messageId
        ? { ...message, status: undefined }
        : message,
    ),
  }))

export const receiveMinervaAnswer = (
  chats: MinervaChats,
  key: string,
  {
    pendingRequest,
    chatId,
    messageId,
    answer,
    navigation,
    timestamp,
  }: {
    pendingRequest: string
    chatId: string
    messageId: string
    answer: string
    navigation?: MinervaNavigation
    timestamp: Date
  },
): MinervaChats => {
  if (chats[key]?.pendingRequest !== pendingRequest) return chats
  return updateChat(chats, key, chat => ({
    ...chat,
    chatId,
    pendingRequest: null,
    pendingActivities: [],
    messages: [
      ...chat.messages,
      {
        messageId,
        role: 'assistant',
        content: answer,
        timestamp,
        ...(chat.pendingActivities.length > 0
          ? { activities: chat.pendingActivities }
          : {}),
        ...(navigation ? { navigation } : {}),
      },
    ],
  }))
}

const endPendingQuestion = (
  chats: MinervaChats,
  key: string,
  pendingRequest: string | null,
  status: 'stopped' | 'failed',
): MinervaChats => {
  const pending = chats[key]?.pendingRequest
  if (!pending || (pendingRequest !== null && pending !== pendingRequest)) {
    return chats
  }
  return updateChat(chats, key, chat => {
    const lastUser = chat.messages.findLastIndex(isMinervaQuestion)
    return {
      ...chat,
      pendingRequest: null,
      pendingActivities: [],
      messages: chat.messages.map((message, index) =>
        index === lastUser ? { ...message, status } : message,
      ),
    }
  })
}

/**
 * The chat is full. The question that hit the limit stays without a status:
 * Retry could never succeed, and the note at the input explains why.
 */
export const reachMinervaTurnLimit = (
  chats: MinervaChats,
  key: string,
  pendingRequest: string,
): MinervaChats => {
  if (chats[key]?.pendingRequest !== pendingRequest) return chats
  return updateChat(chats, key, chat => ({
    ...chat,
    pendingRequest: null,
    pendingActivities: [],
    turnLimitReached: true,
  }))
}

/** What a switch to the dashboard's current timeframe replaces. */
export type MinervaChatSwitchContent = Pick<
  MinervaChatContent,
  'viewContext' | 'metrics' | 'globalFilters'
>

/** Identifies the messages a timeframe choice adds to the chat. */
export type MinervaTimeframeChoiceMessage = {
  messageId: string
  timestamp: Date
}

/**
 * The user's pick shows as their message and the chat confirms it, so the
 * history says where the timeframe changed.
 */
const withTimeframeChoice = (
  messages: MinervaChatMessage[],
  timeframeChoice: MinervaTimeframeChoice,
  { messageId, timestamp }: MinervaTimeframeChoiceMessage,
): MinervaChatMessage[] => [
  ...messages,
  { messageId, role: 'user', content: '', timestamp, timeframeChoice },
  { messageId, role: 'assistant', content: '', timestamp, timeframeChoice },
]

/**
 * Moves the chat onto the dashboard's current context. The backend fixes the
 * context per chat, so the next question starts a new one. The messages and
 * the opening summary stay.
 */
export const switchMinervaChatContext = (
  chats: MinervaChats,
  key: string,
  content: MinervaChatSwitchContent,
  message: MinervaTimeframeChoiceMessage,
): MinervaChats =>
  updateChat(chats, key, chat => ({
    ...chat,
    ...content,
    chatId: null,
    turnLimitReached: false,
    keptTimeframe: null,
    sendsCardSummary: false,
    messages: withTimeframeChoice(
      chat.messages,
      {
        choice: 'switch',
        from: chat.viewContext?.timeframe ?? '',
        to: content.viewContext?.timeframe ?? '',
      },
      message,
    ),
  }))

/** The user keeps chatting on the old data while the dashboard shows `timeframe`. */
export const keepMinervaChatTimeframe = (
  chats: MinervaChats,
  key: string,
  timeframe: string,
  message: MinervaTimeframeChoiceMessage,
): MinervaChats =>
  updateChat(chats, key, chat => ({
    ...chat,
    keptTimeframe: timeframe,
    messages: withTimeframeChoice(
      chat.messages,
      {
        choice: 'keep',
        from: chat.viewContext?.timeframe ?? '',
        to: timeframe,
      },
      message,
    ),
  }))

/** The backend dropped the chat, the next question starts a new one. */
export const resetMinervaChatId = (
  chats: MinervaChats,
  key: string,
): MinervaChats =>
  updateChat(chats, key, chat => ({
    ...chat,
    chatId: null,
    turnLimitReached: false,
  }))

/**
 * Minerva asked for data. Marks the pending question "fetching-data" and
 * keeps the request as an activity, which the answer carries once it lands.
 */
export const receiveMinervaDataRequest = (
  chats: MinervaChats,
  key: string,
  {
    pendingRequest,
    activity,
  }: {
    pendingRequest: string
    activity: MinervaChatActivity
  },
): MinervaChats => {
  if (chats[key]?.pendingRequest !== pendingRequest) return chats
  return updateChat(chats, key, chat => ({
    ...chat,
    pendingActivities: [...chat.pendingActivities, activity],
    messages: chat.messages.map(message =>
      isMinervaQuestion(message) && message.status === undefined
        ? { ...message, status: 'fetching-data' as const }
        : message,
    ),
  }))
}

export const failMinervaQuestion = (
  chats: MinervaChats,
  key: string,
  pendingRequest: string,
): MinervaChats => endPendingQuestion(chats, key, pendingRequest, 'failed')

/** Marks the pending question "stopped". Does nothing while nothing loads. */
export const stopMinervaQuestion = (
  chats: MinervaChats,
  key: string,
): MinervaChats => endPendingQuestion(chats, key, null, 'stopped')

/**
 * A new chat sends the view context, metrics, card summary and catalog.
 * Follow-ups send only the `chatId`; the backend rejects anything else.
 */
export const buildMinervaAskInput = ({
  question,
  messageId,
  chatId,
  viewContext,
  metrics,
  cardSummary,
  catalog,
}: {
  question: string
  messageId: string
  chatId: string | null
  viewContext: MinervaViewContextInput | undefined
  metrics: MinervaMetricInput[] | undefined
  /** Undefined after a timeframe switch, see `sendsCardSummary`. */
  cardSummary: string | undefined
  /** JSON of the cubes the model may ask data from. Only a new chat sends it. */
  catalog: string | null
}): MinervaAskInput =>
  chatId
    ? { question, messageId, chatId }
    : {
        question,
        messageId,
        viewContext,
        metrics,
        ...(cardSummary !== undefined ? { cardSummary } : {}),
        ...(catalog ? { catalog } : {}),
      }

const MINERVA_LOADING_TEXT_KEYS = {
  default: [
    'cubeDashboard_minervaChat_loading_analysingBoard_text',
    'cubeDashboard_minervaChat_loading_readingFeedback_text',
    'cubeDashboard_minervaChat_loading_checkingDisruptions_text',
    'cubeDashboard_minervaChat_loading_evaluatingSources_text',
  ],
  'fleet-performance-overview': [
    'cubeDashboard_minervaChat_loading_analysingBoard_text',
    'cubeDashboard_minervaChat_loading_checkingFleet_text',
    'cubeDashboard_minervaChat_loading_comparingPeriods_text',
    'cubeDashboard_minervaChat_loading_evaluatingSources_text',
  ],
} as const

/** The texts the loading line rotates through, worded for the view. */
export const getMinervaLoadingTextKeys = (viewId: string | undefined) =>
  viewId === 'fleet-performance-overview'
    ? MINERVA_LOADING_TEXT_KEYS[viewId]
    : MINERVA_LOADING_TEXT_KEYS.default

/**
 * The chat answers on the timeframe it was opened with. True once the
 * dashboard shows another one, so the panel can say so.
 */
export const hasMinervaTimeframeChanged = (
  chat: Pick<MinervaChatEntry, 'viewContext'>,
  currentTimeframe: string,
) =>
  chat.viewContext !== undefined &&
  chat.viewContext.timeframe !== currentTimeframe

const hasMinervaErrorCode = (error: unknown, codes: string[]) =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(graphQLError =>
    codes.includes(String(graphQLError.extensions?.code)),
  )

/** The chat has its 20 turns, the backend takes no more questions. */
export const isMinervaTurnLimit = (error: unknown) =>
  hasMinervaErrorCode(error, ['CHAT_TURN_LIMIT'])

/**
 * The backend no longer has the chat: it is older than 24h, or it is unknown.
 * Either way it wants a new one.
 */
export const isMinervaChatGone = (error: unknown) =>
  hasMinervaErrorCode(error, ['CHAT_EXPIRED', 'CHAT_NOT_FOUND'])

/**
 * The panel asks whether to switch to the dashboard's timeframe, unless the
 * user already chose to keep the old one for it.
 */
export const shouldAskMinervaTimeframeSwitch = (
  chat: Pick<MinervaChatEntry, 'viewContext' | 'keptTimeframe'>,
  currentTimeframe: string,
) =>
  hasMinervaTimeframeChanged(chat, currentTimeframe) &&
  chat.keptTimeframe !== currentTimeframe
